import React from 'react';
import { User } from '../../types';
import { Avatar } from '../ui/Avatar';
import { Stars } from '../ui/Stars';
import { sanitize } from '../../lib/security';
import { t } from '../../lib/i18n';
import { useStore } from '../../store';

interface MercenaryCardProps {
  user: User;
  onViewProfile: () => void;
  onMessage: () => void;
  onReport: () => void;
  showActions?: boolean;
}

/* Inline progress bar — no number, just a glowy cyan bar with visible track */
const SkillProgressBar: React.FC<{ name: string; level: number }> = ({ name, level }) => (
  <div style={{ marginBottom: '0.55rem' }}>
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '0.25rem',
    }}>
      <span style={{
        fontFamily: 'var(--font-mono)',
        fontSize: '0.68rem',
        letterSpacing: '0.04em',
        color: 'var(--silver-mid)',
        textTransform: 'uppercase',
      }}>
        {name}
      </span>
    </div>
    {/* Track */}
    <div style={{
      height: '5px',
      background: 'rgba(79,195,247,0.12)',
      border: '1px solid rgba(79,195,247,0.22)',
      borderRadius: '99px',
      overflow: 'hidden',
      position: 'relative',
    }}>
      {/* Fill */}
      <div style={{
        height: '100%',
        width: `${level * 10}%`,
        background: 'linear-gradient(90deg, rgba(45,127,168,0.9), rgba(79,195,247,1))',
        borderRadius: '99px',
        boxShadow: '0 0 8px rgba(79,195,247,0.5)',
        transition: 'width 0.5s cubic-bezier(0.22,0.61,0.36,1)',
      }} />
    </div>
  </div>
);

export const MercenaryCard: React.FC<MercenaryCardProps> = ({
  user, onViewProfile, onMessage, onReport, showActions = true,
}) => {
  const { currentUser, lang } = useStore();
  const isSelf = currentUser?.id === user.id;

  return (
    <div className="merc-card">
      {/* Top — avatar + name + location + stars */}
      <div
        className="merc-card-top"
        onClick={onViewProfile}
        role="button"
        tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter') onViewProfile(); }}
        style={{ cursor: 'pointer' }}
      >
        <Avatar user={user} size={64} />
        <div className="merc-info">
          <h3 className="merc-name">
            {sanitize(user.firstName)} {sanitize(user.lastName)}
          </h3>
          <p className="merc-location" style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.68rem',
            color: 'var(--silver-dim)',
            margin: '0.2rem 0 0.35rem',
            letterSpacing: '0.04em',
          }}>
            📍 {sanitize(user.city)}, {sanitize(user.country)}
          </p>
          <Stars rating={user.rating} count={user.ratingCount} size="sm" />
        </div>
      </div>

      {/* Bio */}
      {user.bio && (
        <p className="merc-bio" style={{
          fontSize: '0.8rem',
          color: 'var(--silver-mid)',
          fontStyle: 'italic',
          lineHeight: 1.6,
          margin: '0.75rem 0 0.65rem',
          paddingLeft: '0.6rem',
          borderLeft: '2px solid var(--cyan-border)',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          {sanitize(user.bio)}
        </p>
      )}

      {/* Skills — progress bars, no numbers */}
      {user.skills.length > 0 && (
        <div style={{ margin: '0.65rem 0 0.5rem' }}>
          {user.skills.slice(0, 3).map(s => (
            <SkillProgressBar key={s.name} name={s.name} level={s.level} />
          ))}
          {user.skills.length > 3 && (
            <p style={{
              fontFamily: 'var(--font-mono)',
              fontSize: '0.62rem',
              color: 'var(--cyan-dim)',
              letterSpacing: '0.08em',
              marginTop: '0.3rem',
            }}>
              +{user.skills.length - 3} {lang === 'fr' ? 'compétences' : lang === 'es' ? 'habilidades' : 'more skills'}
            </p>
          )}
        </div>
      )}

      {/* Actions */}
      {showActions && (
        <div className="merc-card-actions" style={{
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'center',
          marginTop: '0.85rem',
          paddingTop: '0.75rem',
          borderTop: '1px solid rgba(79,195,247,0.1)',
        }}>
          <button className="btn btn-outline btn-sm" onClick={onViewProfile} style={{ flex: 1 }}>
            {t(lang, 'mercs_view')}
          </button>
          {currentUser && !isSelf && (
            <button className="btn btn-gold btn-sm" onClick={onMessage} style={{ flex: 1 }}>
              💬 {t(lang, 'mercs_message')}
            </button>
          )}
          {currentUser && !isSelf && (
            <button
              className="report-btn"
              onClick={onReport}
              aria-label="Report user"
              style={{
                background: 'none',
                border: '1px solid var(--navy-border)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--silver-dim)',
                cursor: 'pointer',
                padding: '0.3rem 0.5rem',
                fontSize: '0.75rem',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'rgba(239,83,80,0.4)';
                e.currentTarget.style.color = 'var(--red)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'var(--navy-border)';
                e.currentTarget.style.color = 'var(--silver-dim)';
              }}
            >
              ⚠
            </button>
          )}
        </div>
      )}
    </div>
  );
};
